/**
 * IndexedDB Service for persistent storage
 * Data persists even when browser is closed
 */

const DB_NAME = 'OcktraDB';
const DB_VERSION = 1;

export interface StoredData<T> {
    key: string;
    data: T;
    timestamp: number;
    expiresAt: number;
}

class IndexedDBService {
    private db: IDBDatabase | null = null;
    private initPromise: Promise<void> | null = null;

    /**
     * Initialize the database
     */
    async init(): Promise<void> {
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                console.error('[IndexedDB] Error opening database:', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                console.log('[IndexedDB] Database opened successfully');
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;

                // Create object stores if they don't exist
                if (!db.objectStoreNames.contains('cache')) {
                    const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
                    cacheStore.createIndex('expiresAt', 'expiresAt', { unique: false });
                    console.log('[IndexedDB] Created cache object store');
                }

                if (!db.objectStoreNames.contains('instruments')) {
                    db.createObjectStore('instruments', { keyPath: 'key' });
                    console.log('[IndexedDB] Created instruments object store');
                }
            };
        });

        return this.initPromise;
    }

    /**
     * Set data in the specified store with optional expiry
     */
    async set<T>(storeName: string, key: string, data: T, ttlMs?: number): Promise<void> {
        await this.init();

        if (!this.db) {
            throw new Error('Database not initialized');
        }

        const now = Date.now();
        const storedData: StoredData<T> = {
            key,
            data,
            timestamp: now,
            expiresAt: ttlMs ? now + ttlMs : Number.MAX_SAFE_INTEGER
        };

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([storeName], 'readwrite');
            const store = transaction.objectStore(storeName);
            const request = store.put(storedData);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get data from the specified store
     * Returns null if expired or not found
     */
    async get<T>(storeName: string, key: string): Promise<T | null> {
        await this.init();

        if (!this.db) {
            throw new Error('Database not initialized');
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([storeName], 'readonly');
            const store = transaction.objectStore(storeName);
            const request = store.get(key);

            request.onsuccess = () => {
                const result = request.result as StoredData<T> | undefined;

                if (!result) {
                    resolve(null);
                    return;
                }

                // Check if data has expired
                if (result.expiresAt < Date.now()) {
                    console.log(`[IndexedDB] Data expired for key: ${key}`);
                    // Delete expired data
                    this.delete(storeName, key);
                    resolve(null);
                    return;
                }

                resolve(result.data);
            };

            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Delete data from the specified store
     */
    async delete(storeName: string, key: string): Promise<void> {
        await this.init();

        if (!this.db) {
            throw new Error('Database not initialized');
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([storeName], 'readwrite');
            const store = transaction.objectStore(storeName);
            const request = store.delete(key);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Clear all data from the specified store
     */
    async clear(storeName: string): Promise<void> {
        await this.init();

        if (!this.db) {
            throw new Error('Database not initialized');
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([storeName], 'readwrite');
            const store = transaction.objectStore(storeName);
            const request = store.clear();

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Clean up expired entries from all stores
     */
    async cleanupExpired(): Promise<void> {
        await this.init();

        if (!this.db) {
            throw new Error('Database not initialized');
        }

        const stores = ['cache', 'instruments'];
        const now = Date.now();

        for (const storeName of stores) {
            await new Promise<void>((resolve, reject) => {
                const transaction = this.db!.transaction([storeName], 'readwrite');
                const store = transaction.objectStore(storeName);
                const request = store.openCursor();

                request.onsuccess = (event) => {
                    const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null;

                    if (cursor) {
                        const data = cursor.value as StoredData<unknown>;
                        if (data.expiresAt < now) {
                            cursor.delete();
                            console.log(`[IndexedDB] Deleted expired entry: ${data.key}`);
                        }
                        cursor.continue();
                    } else {
                        resolve();
                    }
                };

                request.onerror = () => reject(request.error);
            });
        }
    }
}

// Export singleton instance
export const indexedDBService = new IndexedDBService();
